import StaffApi from '../../api/staffmanagement';
import { StaffManagementAreas, StaffManagementBays, StaffMtpEmployee, BayTailInfo } from '../../types/staffManagement';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ToolsApi from '../../api/tools';
import { Button, Chip, LinearProgress, Tabs, Dialog, SelectMultiple } from '../../tailwindcomponents';
import GroupList from './GroupList';
import AboutEmployee from './AboutEmployee';
import CardComponent from './CardComponent';
import useGetWindowSize from '../../components/Hooks/useGetWindowSize';
import UserApi from '../../api/userApi';
import { User } from '../../types/user';
import { ToolData } from '../../types/tool';
import { useSelector } from "react-redux";
import { RootState } from "../../reducer";
import SkillsApi from '../../api/skillsApi';
import { SelectObject } from './../../types/certificates';
import staffApi from "../../api/staffmanagement";
import SecurityApi from '../../api/security';
import OverviewApi from '../../api/overview';
import { useQueryTab } from '../../utils/usePersistedTab';

const attendanceStatusOptions = [
    { id: 0, name: "Away" },
    { id: 1, name: "In Work" },
    { id: 2, name: "Night Shift" },
    { id: 3, name: "Vacation/Absence" },
];

type AttendanceStatusCount = {
    attendanceStatus: string,
}

export interface DragInfo {
    draggingFromBay: StaffManagementBays | undefined,
    draggingFromGroup: StaffManagementAreas | undefined,
    draggingFromBayIndex: number,
    draggingFromGroupIndex: number,
    draggedEmployeeIndex: number,
    fromSearchDraggedEmployee: string,
    fromSearchDraggedEmployeeNr: number,
    fromSearchDraggedEmployeeStatus: string
}

const StaffManagement = () => {
    const [windowWidth, windowHeight] = useGetWindowSize();
    const { tabValue, setTabValue } = useQueryTab("tab", 0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const store = useSelector((state: RootState) => state);
    const [staffManagementBaysLoaded, setStaffManagementBaysLoaded] = useState<boolean>(false);
    const [aboutEmployeeDialogOpen, setAboutEmployeeDialogOpen] = useState<boolean>(false);
    const [staffManagementBays, setStaffManagementBays] = useState<StaffManagementBays[]>([]);
    const [allEmployees, setAllEmployees] = useState<User[]>([]);
    const [selectedUser, setSelectedUser] = useState<User | StaffMtpEmployee | null>();
    const [showSearch, setShowSearch] = useState<boolean>(false);
    const [dragging, setDragging] = useState<boolean>(false);
    const [selectedAttendanceStatus, setSelectedAttendanceStatus] = useState<number[]>([0, 1, 2, 3]);
    const [selectedSkillStatus, setSelectedSkillStatus] = useState<number[]>([]);
    const [distinctBays, setDistinctBays] = useState<string[]>();
    const [skillOptions, setSkillOptions] = useState<SelectObject[]>([]);


    const dragInfo = useRef<DragInfo>({
        draggingFromBay: undefined,
        draggingFromGroup: undefined,
        draggingFromBayIndex: -1,
        draggingFromGroupIndex: -1,
        draggedEmployeeIndex: -1,
        fromSearchDraggedEmployee: "",
        fromSearchDraggedEmployeeNr: -1,
        fromSearchDraggedEmployeeStatus: "Away"
    });

    const meGroups = store.user.me.user_clearence_groups;

    const isStmAdmin = useMemo(() => {
        return meGroups.includes("Staff Management Edit");
    }, [])

    const isSkillsView = meGroups.includes("Mechanic Skills Edit") || meGroups.includes("Mechanic Skills View");
    const [attendanceStatus, setAttendanceStatus] = useState<AttendanceStatusCount[]>([]);
    const [workpackagesInBay, setWorkpackagesInBay] = useState<BayTailInfo[]>([]);

    useEffect(() => {
        refresh();
    }, []);

    const refresh = useCallback(async () => {
        setIsLoading(true);

        try {
            getAllMaintenerUsers();

            const [
                staffRes,
                toolsRes,
                skillsRes,
                hangarRes,
            ] = await Promise.all([
                StaffApi.GetAllStaffManagement(),
                ToolsApi.getAllLoanedToolsbyDays(0, "All positions", 0, "", true, false),
                SkillsApi.GetSkills(),
                SecurityApi.AircraftInHangar(),
            ]);

            /* =========================
               STAFF MANAGEMENT
            ========================== */
            let bays: StaffManagementBays[] = staffRes.data;

            const statusArray: AttendanceStatusCount[] = [];
            bays.forEach(bay =>
                bay.areas.forEach(area =>
                    area.employyes_in_group.forEach(emp =>
                        statusArray.push({ attendanceStatus: emp.employee_attendance_status })
                    )
                )
            );

            // enrich staffManagementBays with tool info
            const toolMap = buildToolStatusMap(toolsRes);
            bays = updateEmployeesWithTools(bays, toolMap);

            setStaffManagementBays(bays);
            setAttendanceStatus(statusArray);

            staffRes.bays.unshift("All Bays");
            setDistinctBays(staffRes.bays);

            !staffManagementBaysLoaded && setStaffManagementBaysLoaded(true);

            /* =========================
               SKILLS
            ========================== */
            if (skillsRes.status === true && skillOptions.length === 0) {
                const filteredSkills: SelectObject[] = skillsRes.data
                    .filter(skill => skill.Visible)
                    .map((skill, index) => ({ name: skill.Skill, id: index }));

                setSkillOptions(filteredSkills);
            }

            /* =========================
               HANGAR / WORK PACKAGES
            ========================== */
            const bayNames = hangarRes.data.map(b => b.bay_name);

            const packagePromises = hangarRes.data.map((bay, index) =>
                OverviewApi.GetPackageInformationByTailNumber(
                    bay.tail_number,
                    ["IN WORK"],
                    false
                ).then(pkgRes => ({
                    tailNumber: bay.tail_number,
                    bay: bayNames[index],
                    workPackages: pkgRes.status ? pkgRes.data : [],
                }))
            );

            const workPackages = await Promise.all(packagePromises);
            setWorkpackagesInBay(workPackages);

        } catch (err) {
            console.error("Refresh failed", err);
        } finally {
            setIsLoading(false);
        }
    }, [staffManagementBaysLoaded, skillOptions]);

    const updateEmployeesWithTools = (bays: StaffManagementBays[], toolMap: Map<number, { hasToolLoanedOut: boolean; isToolsLoanedOutToLong: boolean; toolDescription: string | null }>): StaffManagementBays[] => {
        return bays.map(bay => ({
            ...bay,
            areas: bay.areas.map(area => ({
                ...area,
                employyes_in_group: area.employyes_in_group.map(emp => {
                    const toolStatus = toolMap.get(emp.user_id);

                    return {
                        ...emp,
                        hasToolLoanedOut: toolStatus?.hasToolLoanedOut ?? false,
                        isToolsLoanedOutToLong: toolStatus?.isToolsLoanedOutToLong ?? false,
                        toolDescription: toolStatus?.toolDescription ?? null,
                    };
                }),
            })),
        }));
    };

    const buildToolStatusMap = (tools: ToolData[]) => {
        const map = new Map<
            number,
            { hasToolLoanedOut: boolean; isToolsLoanedOutToLong: boolean; toolDescription: string | null }
        >();

        tools.forEach(tool => {
            const current = map.get(tool.employee_id);

            map.set(tool.employee_id, {
                hasToolLoanedOut: true,
                isToolsLoanedOutToLong:
                    (current?.isToolsLoanedOutToLong ?? false) ||
                    tool.tool_to_long_in_circulations === true,
                toolDescription: tool?.description ?? "",
            });
        });

        return map;
    };


    const getAllMaintenerUsers = () => {
        UserApi.getAllUsers().then(res => {
            setAllEmployees(res);
        });
    }

    //This function is used to scroll the page when the user is dragging an employee.
    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        const mouseY = e.clientY;
        if (mouseY < 100 && dragging) {
            window.scrollTo({
                top: window.pageYOffset - 10,
            });
        }
        else if (mouseY > window.innerHeight - 100) {
            window.scrollTo({
                top: window.pageYOffset + 10,
            });
        }
        e.preventDefault();
    };

    const handleAboutEmployeeDialogOpen = useCallback((dialogOpen: boolean, user: StaffMtpEmployee | User | null) => {
        setSelectedUser(user);
        setAboutEmployeeDialogOpen(dialogOpen);
    }, [setSelectedUser, setAboutEmployeeDialogOpen]);

    const returnAttendanceStatusString = useMemo(() => {
        const array = [] as Array<string>;
        selectedAttendanceStatus.map(item => {
            if (item === 3) {
                array.push("Vacation");
            }
            else {
                array.push(attendanceStatusOptions[item].name);
            }
        })
        return array;
    }, [selectedAttendanceStatus]);

    const returnMechanicsSkillsString = useMemo(() => {
        const array = [] as Array<string>;
        selectedSkillStatus.map(item => {
            array.push(skillOptions[item].name);
        })
        return array;
    }
        , [selectedSkillStatus, skillOptions]);

    const memoObj = useMemo(() => { return { bay: "", areas: [] } }, [])

    const moveEmployee = useCallback((destinationAreaIndex: number, bay: StaffManagementBays) => {
        const destinationArea = bay.areas[destinationAreaIndex];
        let movedEmployee: StaffMtpEmployee | undefined;
        let isSearchComponent = false;

        if (dragInfo?.current?.draggingFromBayIndex === -1 && dragInfo?.current?.draggingFromGroupIndex === -1) {//if the dragged employee is from the search component.
            const addBay = bay.bay;
            const addArea = bay.areas[destinationAreaIndex].area;
            const workstation_name = `${addBay} in ${addArea}`;
            const employee = allEmployees.find((employee) => employee.Id === dragInfo?.current?.fromSearchDraggedEmployeeNr);
            movedEmployee = {
                user_id: dragInfo.current.fromSearchDraggedEmployeeNr,
                employee_name: dragInfo.current.fromSearchDraggedEmployee,
                workstation_name: workstation_name,
                user_name: employee ? employee.userName : "",
                employee_attendance_status: dragInfo.current.fromSearchDraggedEmployeeStatus,
                user_image: employee ? employee.user_image : "",
                mobile: employee ? employee.mobile : "",
                job_title: employee ? employee.job_title : "",
                crs_number: employee ? employee.crs_number : "",
                employee_abbreviation: employee ? employee.employee_abbreviation : "",
                employee_skills: [] as string[],
                is_team_leader: false
            };
            isSearchComponent = true;
        }
        else {

            // Get the source and destination areas
            // ! Recommend using other mehcanism that '!' but I'm fairly sure it will never be null
            const sourceArea = dragInfo!.current!.draggingFromBay?.areas[dragInfo!.current!.draggingFromGroupIndex];
            // Remove the employee from the source area
            // ! Recommend using other mehcanism that '!' but I'm fairly sure it will never be null
            movedEmployee = sourceArea?.employyes_in_group.at(dragInfo!.current!.draggedEmployeeIndex);
        }
        // Add the employee to the destination area
        // destinationArea.employyes_in_group.push(movedEmployee);
        if (movedEmployee) {
            const username = movedEmployee.user_name.split('@')[0];
            const mgtBayCopy = [...staffManagementBays];
            const newArr = mgtBayCopy.map((mapBay) => {
                if (mapBay.bay === bay.bay) {
                    const newAreas = mapBay.areas.map((area) => {
                        if ((area.area === destinationArea.area) && movedEmployee) {
                            area.employyes_in_group.push(movedEmployee);
                        }
                        return area;
                    });
                    mapBay.areas = newAreas;
                }
                if (mapBay.bay === dragInfo.current?.draggingFromBay?.bay) {
                    const newAreas = mapBay.areas.map((area) => {
                        if ((area.area === dragInfo!.current!.draggingFromBay?.areas[dragInfo!.current!.draggingFromGroupIndex].area) && movedEmployee) {
                            area.employyes_in_group = area.employyes_in_group.filter((emp) => emp.user_name !== username);
                        }
                        return area;
                    });
                    mapBay.areas = newAreas;
                }
                return mapBay;
            })
            setStaffManagementBays(newArr);
            if (isSearchComponent) {
                setIsLoading(true);
                onAddEmployeeToGroup(username, destinationArea.area, false);
            }
            else {
                setIsLoading(true);
                const sourceArea = dragInfo!.current!.draggingFromBay?.areas[dragInfo?.current?.draggingFromGroupIndex ?? -1];
                if (sourceArea) {
                    staffApi.DeleteUserFromGroup(username, sourceArea?.area).then((res) => {
                        const isLeaderId = movedEmployee?.user_id;
                        const isLeader = movedEmployee?.is_team_leader === undefined ? false : movedEmployee?.is_team_leader;
                        if (isLeader && isLeaderId) {
                            staffApi.RemoveLeaderRole(isLeaderId, sourceArea?.area).then((res) => { });
                        }
                        onAddEmployeeToGroup(username, destinationArea.area, isLeader);
                    });
                }
            }
        }
    }, [staffManagementBays]);

    const onAddEmployeeToGroup = useCallback((userName: string, area: string, refreshTrue: boolean) => {
        staffApi.AssignUserToGroup(userName, area).then((res) => {
            setIsLoading(false);
            if (res.isPageRefresh === true || refreshTrue) {
                refresh();
            }
        });
    }, [refresh]);

    return (
        <div onDragOver={(e: React.DragEvent<HTMLDivElement>) => handleDragOver(e)}>
            <div>
                {isLoading && <LinearProgress />}
            </div>
            <Tabs
                tabNames={distinctBays ? distinctBays : []}
                onChangeTabValue={(tabValue: number) => { setTabValue(tabValue) }}
                tabValue={tabValue}
            />
            <div className="w-full">
                {isStmAdmin &&
                    <div className={`flex items-center flex-wrap sticky top-0 z-20 bg-background py-1 px-2.5 ${showSearch ? 'sm:w-1/2 md:w-2/3 lg:w-[79.166667%] xl:w-5/6' : 'sm:w-full md:w-full lg:w-full xl:w-full'}`}
                    >
                        <div className="flex items-center flex-wrap">
                            <SelectMultiple
                                optionsObject={attendanceStatusOptions}
                                onSelectedItem={(e: any) => setSelectedAttendanceStatus(e.target.value)}
                                selectedItems={selectedAttendanceStatus}
                                width={200}
                                filter_by_text={"Filter by Attendance"}
                            />
                            {isSkillsView &&
                                                            <div className='ml-2'>

                                < SelectMultiple
                                    optionsObject={skillOptions}
                                    onSelectedItem={(e: any) => setSelectedSkillStatus(e.target.value)}
                                    selectedItems={selectedSkillStatus}
                                    width={200}
                                    filter_by_text={"Filter by Mechanic Skill"}
                                />
                            </div>
                            }
                            <Button className="mt-5 ml-4" variant="contained" color="primary" size="small" onClick={() => setShowSearch(!showSearch)} >{showSearch ? "Hide Search" : "Show Search"}</Button>
                        </div>
                        <div className="flex items-center mt-4 ml-4">
                            <Chip className="mr-2 text-sm font-bold" style={{ backgroundColor: 'orange' }} label={attendanceStatus.filter(status => status.attendanceStatus === "Away").length} variant="filled" />
                            <Chip className="mr-2 text-sm font-bold" style={{ backgroundColor: '#02c412' }} label={attendanceStatus.filter(status => status.attendanceStatus === "In Work").length} variant="filled" />
                            <Chip className="mr-2 text-sm font-bold" style={{ backgroundColor: '#028cf5' }} label={attendanceStatus.filter(status => status.attendanceStatus === "Night Shift").length} variant="filled" />
                            <Chip className="mr-2 text-sm font-bold" style={{ backgroundColor: 'pink' }} label={attendanceStatus.filter(status => status.attendanceStatus === "Vacation").length} variant="filled" />
                        </div>
                    </div>
                }
                {(tabValue === 0 && staffManagementBaysLoaded) &&
                    <div className="flex flex-row flex-wrap p-2.5">
                        <div className={showSearch ? 'sm:w-1/2 md:w-2/3 lg:w-[79.166667%] xl:w-5/6' : 'sm:w-full md:w-full lg:w-full xl:w-full'}>
                            <div style={windowWidth < 1700 ? { columnCount: '1', columnGap: '16px' } : windowWidth <= 2400 ? { columnCount: '2', columnGap: '16px' } : { columnCount: '3', columnGap: '16px' }}>
                                {staffManagementBays.map((bay, bayIndex) => (
                                    <div key={bayIndex} className="inline-block mb-4 w-full" >
                                        <CardComponent
                                            bay={bay.bay}
                                            banner_color='black'
                                            bayTailInfo={workpackagesInBay.find((workPackage: BayTailInfo) => bay.bay?.toLowerCase().includes(workPackage?.bay?.toLowerCase()))}
                                            isbay={true}
                                        >
                                            <GroupList
                                                setStaffManagementBays={setStaffManagementBays}
                                                isStmAdmin={isStmAdmin}
                                                setDragging={setDragging}
                                                bay={bay}
                                                bayIndex={bayIndex}
                                                isSearchComponent={false}
                                                employees={allEmployees}
                                                setLoading={setIsLoading}
                                                handleAboutEmployeeDialog={handleAboutEmployeeDialogOpen}
                                                attendanceStatus={returnAttendanceStatusString}
                                                mechanicsSkills={returnMechanicsSkillsString}
                                                dragInfo={dragInfo}
                                                moveEmployee={moveEmployee}
                                                tabValue={tabValue}
                                            />
                                        </CardComponent >
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="sm:w-1/2 md:w-1/3 lg:w-[20.833333%] xl:w-1/6 sticky top-0 right-0" style={{ visibility: showSearch ? 'visible' : 'hidden' }}>
                            <div className="fixed top-[140px] overflow-y-auto">
                                <div className="w-[300px] p-1.5 m-1.5" style={{ height: `${windowHeight - 175}px` }}>
                                    {
                                        <GroupList
                                            setStaffManagementBays={setStaffManagementBays}
                                            isStmAdmin={isStmAdmin}
                                            setDragging={setDragging}
                                            bay={memoObj}
                                            bayIndex={-1}
                                            isSearchComponent={true}
                                            employees={allEmployees}
                                            setLoading={setIsLoading}
                                            handleAboutEmployeeDialog={handleAboutEmployeeDialogOpen}
                                            attendanceStatus={returnAttendanceStatusString}
                                            mechanicsSkills={returnMechanicsSkillsString}
                                            dragInfo={dragInfo}
                                            moveEmployee={moveEmployee}
                                            tabValue={tabValue}
                                        />
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                }
                {
                    tabValue !== 0 &&
                    <div className="flex flex-row flex-wrap p-2.5">
                        <div className="sm:w-1/2 md:w-2/3 lg:w-3/4 xl:w-5/6" >
                            <div style={windowWidth < 1700 ? { columnCount: '1', columnGap: '16px' } : windowWidth <= 2400 ? { columnCount: '2', columnGap: '16px' } : { columnCount: '3', columnGap: '16px' }}>
                                <GroupList
                                    setStaffManagementBays={setStaffManagementBays}
                                    isStmAdmin={isStmAdmin}
                                    setDragging={setDragging}
                                    bayIndex={tabValue - 1}
                                    bay={staffManagementBays[tabValue - 1]}
                                    isSearchComponent={false}
                                    employees={allEmployees}
                                    setLoading={setIsLoading}
                                    handleAboutEmployeeDialog={handleAboutEmployeeDialogOpen}
                                    attendanceStatus={returnAttendanceStatusString}
                                    mechanicsSkills={returnMechanicsSkillsString}
                                    dragInfo={dragInfo}
                                    moveEmployee={moveEmployee}
                                    tabValue={tabValue}
                                />
                            </div>
                        </div>
                        {((showSearch) && isStmAdmin) &&
                            <div className="sm:w-1/2 md:w-1/3 lg:w-1/4 xl:w-1/6 sticky top-0">
                                <div className="fixed top-[140px] overflow-y-auto">
                                    <div className="w-[300px] p-1.5 m-1.5" style={{ height: `${windowHeight - 175}px` }}>
                                        {showSearch &&
                                            <GroupList
                                                setStaffManagementBays={setStaffManagementBays}
                                                isStmAdmin={isStmAdmin}
                                                setDragging={setDragging}
                                                bay={{ bay: "", areas: [] }}
                                                bayIndex={-1}
                                                isSearchComponent={true}
                                                employees={allEmployees}
                                                setLoading={setIsLoading}
                                                handleAboutEmployeeDialog={handleAboutEmployeeDialogOpen}
                                                attendanceStatus={returnAttendanceStatusString}
                                                mechanicsSkills={returnMechanicsSkillsString}
                                                dragInfo={dragInfo}
                                                moveEmployee={moveEmployee}
                                                tabValue={tabValue}
                                            />
                                        }
                                    </div>
                                </div>
                            </div>
                        }
                    </div>
                }
            </div>
            <Dialog
                visible={aboutEmployeeDialogOpen}
                max_width={"md"} title={selectedUser ? selectedUser!.employee_name : ""}
                context={
                    <div className="w-full">
                        <AboutEmployee
                            tabNames={isSkillsView ? ["About employee", "Tools", "Certificate", "Skills", "Team leader"] : ["About employee", "Tools", "Certificate", "Team leader"]}
                            user={selectedUser!}
                            refresh={refresh}
                        />
                    </div>
                }
                onClose={(status: any) => (setAboutEmployeeDialogOpen(false))}
                strict={false}
                show_options={false}
            />
        </div>
    )
}
export default StaffManagement;